import { Options } from "components/haven-design/snackbar/use-snackbar";
import { invoicedTrackingQueries_invoicedTrackings as invoicedTrackings } from "types/gql-types";

type TrackingStatus = Pick<invoicedTrackings, "id" | "queuedForInvoiced" | "invoicedSendError">;

/** Ids still queued or running. A row missing from the results is unknown, so it stays pending. */
export const stillSending = (ids: string[], trackings: TrackingStatus[]): string[] => {
  const byId = new Map(trackings.map((tracking) => [tracking.id, tracking]));

  return ids.filter((id) => byId.get(id)?.queuedForInvoiced ?? true);
};

export const sendSummary = (ids: string[], trackings: TrackingStatus[]): Options => {
  const pendingIds = new Set(stillSending(ids, trackings));
  const failed = trackings.filter(({ id, invoicedSendError }) =>
    ids.includes(id) && !pendingIds.has(id) && invoicedSendError).length;
  const pending = pendingIds.size;
  const sent = ids.length - failed - pending;

  if (failed === 0 && pending === 0) {
    return { message: `Sent ${sent} to Invoiced.`, status: "success" };
  }

  const parts = [`${sent} sent`, `${failed} failed`];
  if (pending > 0) parts.push(`${pending} still sending (refresh later)`);

  return { message: `${parts.join(", ")}. See the Send Error column.`, status: failed > 0 ? "error" : "warning" };
};

/** Unsent rows with letters, plus sent rows whose last send failed (e.g. the email step). */
export const sendableIds = (
  trackings: Array<Pick<invoicedTrackings, "id" | "sentToInvoiced" | "invoicedSendError" | "regularQuantity" | "overageQuantity">>,
): string[] =>
  trackings
    .filter(({ sentToInvoiced, invoicedSendError, regularQuantity, overageQuantity }) =>
      (!sentToInvoiced || Boolean(invoicedSendError)) && (regularQuantity > 0 || overageQuantity > 0))
    .map(({ id }) => id);
